import type { NotificationItem, NotificationKind } from '@todo/shared';
import type { ModuleId } from '../lib/modules';
import { MODULE_BRAND_ICON, MODULE_BRAND_COLOR } from '../lib/moduleIcons';

/**
 * One notification row, shared by the bell dropdown and the full
 * Notifications page so both look identical.
 *
 * EACH ROW LEADS WITH ITS MODULE'S BRAND MARK - the To-Do tick, the Meetings
 * mark, the CG Sign mark, the Folder mark - tinted with that module's
 * accent. It used to be the actor's initials, or a neutral gear for
 * anything the system raised, which meant the two most common rows in a
 * busy inbox (a meeting reminder and a signature chase) wore the same grey
 * cog and could only be told apart by reading them. The mark answers
 * "which part of the app is this about?" before the title is read, and it
 * matches what the rail and the module picker already show for the same
 * thing.
 *
 * The actor is not lost: every notification body that has one names them in
 * words ("Sreenivassan is waiting for your signature"), which an avatar
 * never did.
 *
 * The "currently open" ring uses a dedicated pink/magenta accent rather than
 * the app's usual purple brand color - scoped to this one component, it is
 * what makes the open row read as clearly "this is what's on screen right
 * now" the way the reference design's highlighted card does, without
 * recoloring every button and link elsewhere in the app.
 */

/**
 * Which module a notification belongs to.
 *
 * A Record rather than a prefix test on the string: adding a seventh kind
 * to NotificationKind becomes a COMPILE ERROR here until it is given a
 * home, where `kind.startsWith('TASK_')` would silently fall through to a
 * default and ship a wrong icon. The same reasoning as MODULE_BRAND_ICON
 * being keyed by ModuleId.
 */
const KIND_MODULE: Record<NotificationKind, ModuleId> = {
  TASK_ASSIGNED: 'todo',
  TASK_COMPLETED: 'todo',
  TASK_REMINDER: 'todo',
  TASK_CANCELLED: 'todo',
  TASK_BLOCKED: 'todo',
  TASK_COMMENTED: 'todo',
  MEETING_INVITED: 'meetings',
  MEETING_REMINDER: 'meetings',
  SIGN_REQUESTED: 'sign',
  SIGN_SIGNED: 'sign',
  SIGN_DECLINED: 'sign',
  SIGN_COMPLETED: 'sign',
  SIGN_DELEGATED: 'sign',
  SIGN_REMINDER: 'sign',
  FOLDER_SHARED: 'vault',
};

/**
 * The module's mark on a tinted plate.
 *
 * The tint is the module's own accent at 14% alpha with a 30% ring, mixed
 * from the one hex in MODULE_BRAND_COLOR rather than hand-picked per
 * module - six hard-coded Tailwind pairs would drift the moment a brand
 * colour changed, and the marks already carry those colours internally.
 *
 * Inline styles, not classes, for the same reason: these are brand values
 * that live outside the Tailwind palette, and `bg-[#A187F5]/14` cannot be
 * built from a runtime variable anyway.
 */
function ModuleMark({ kind }: { kind: NotificationKind }) {
  const moduleId = KIND_MODULE[kind];
  const Icon = MODULE_BRAND_ICON[moduleId];
  const accent = MODULE_BRAND_COLOR[moduleId];

  return (
    <span
      className="grid h-11 w-11 shrink-0 place-items-center rounded-full ring-1"
      style={{
        backgroundColor: `${accent}24`,
        // Tailwind's ring-* cannot take a runtime value; this is the ring.
        boxShadow: `inset 0 0 0 1px ${accent}4D`,
      }}
    >
      <Icon className="h-6 w-6" />
    </span>
  );
}

export function NotificationCard({
  item,
  active,
  timeLabel,
  onOpen,
}: {
  item: NotificationItem;
  /** This row's task is the one currently open in the side drawer. */
  active: boolean;
  /** Pre-formatted timestamp - the bell uses "3m ago", the page a full date. */
  timeLabel: string;
  onOpen: () => void;
}) {
  const unread = !item.readAt;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={[
        'flex w-full items-start gap-3 rounded-xl px-4 py-3.5 text-left ring-1 transition',
        active
          ? 'bg-fuchsia-500/5 ring-2 ring-fuchsia-500'
          : unread
            ? 'bg-white ring-slate-200 hover:bg-slate-50'
            : 'bg-slate-50/60 ring-slate-100 hover:bg-slate-50',
      ].join(' ')}
    >
      <ModuleMark kind={item.kind} />
      <span className="min-w-0 flex-1">
        <span
          className={[
            'block text-sm leading-snug',
            unread ? 'font-medium text-slate-900' : 'text-slate-600',
          ].join(' ')}
        >
          {item.title}
        </span>
        {item.body && (
          <span className="mt-0.5 block text-xs leading-snug text-slate-500">{item.body}</span>
        )}
        <span className="mt-1.5 block text-xs text-slate-400">{timeLabel}</span>
      </span>
      {unread && (
        <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-fuchsia-500" />
      )}
    </button>
  );
}
